export type MessageValue = string | number;
export type MessageParams = Readonly<Record<string, MessageValue>>;

const EN = {
  "app.title": "STORMGLASS",
  "app.subtitle": "Weather Chamber Defense",
  "action.play": "Begin experiment",
  "action.continue": "Resume run",
  "action.restart": "Run it again",
  "action.pause": "Pause",
  "action.resume": "Resume",
  "action.settings": "Settings",
  "action.shop": "Chamber lab",
  "action.install": "Install",
  "action.back": "Back",
  "action.dismiss": "Got it",
  "action.purchase": "Purchase",
  "action.select": "Select",
  "hud.round": "Round {round}",
  "hud.quota": "{remaining} incoming",
  "hud.integrity": "Chamber integrity",
  "hud.charge": "Charge",
  "hud.condensate": "Condensate",
  "hud.speed": "Simulation speed",
  "hud.bossTimer": "{seconds}s to impact",
  "hud.status":
    "Round {round}. {remaining} threats remain. Integrity {integrity} of {maxIntegrity}. Charge {charge}.",
  "home.eyebrow": "ATMOSPHERIC DEFENSE ARRAY",
  "home.lede":
    "Hold the eye of the storm. Shape lightning, rain, wind and hail into one living defense system.",
  "home.offline": "Offline ready after first load",
  "home.local": "Progress stays on this device",
  "game.targeting": "Priority Bolt targets the threat nearest the chamber.",
  "game.firstEnemy":
    "Contact at the perimeter. The chamber fires automatically—watch the distance ring.",
  "game.firstUpgrade":
    "Charge stabilized. Spend it below to strengthen Priority Bolt.",
  "game.paused": "Experiment suspended",
  "game.pausedDetail": "The simulation is frozen. No time passes while paused.",
  "upgrade.damage": "Amplify bolt",
  "upgrade.damage.description": "+4 Lightning damage",
  "upgrade.interval": "Tighten interval",
  "upgrade.interval.description": "Fire 6% faster",
  "upgrade.critical": "Tune polarity",
  "upgrade.critical.description": "+1.5% critical chance",
  "upgrade.criticalDamage": "Surge capacitor",
  "upgrade.criticalDamage.description": "+15% critical damage",
  "milestone.title": "Atmospheric breakthrough",
  "milestone.detail":
    "Choose one transformation. The simulation remains paused.",
  "milestone.chain": "Chain Lightning",
  "milestone.chain.description":
    "Bolts arc from the primary target into nearby threats.",
  "milestone.overcharge": "Overcharge",
  "milestone.overcharge.description":
    "Every sixth discharge lands with overwhelming force.",
  "milestone.execution": "Execution Voltage",
  "milestone.execution.description":
    "Deal increased damage to threats close to the chamber.",
  "results.title": "Atmosphere collapsed",
  "results.bossTitle": "Boss reached the chamber",
  "results.summary":
    "The chamber held through round {round}. Condensate recovered: {condensate}.",
  "results.defeated": "Threats neutralized",
  "results.bosses": "Bosses dispersed",
  "results.best": "Best round",
  "results.weapon": "Most effective weather",
  "results.reaction": "Top reaction",
  "shop.title": "Chamber laboratory",
  "shop.detail": "Permanent systems join every future experiment.",
  "shop.locked": "Reach round {round} to reveal",
  "shop.access": "Research available",
  "settings.title": "Settings",
  "settings.quality": "Visual quality",
  "settings.motion": "Reduce motion",
  "settings.contrast": "High contrast",
  "settings.numbers": "Damage numbers",
  "settings.master": "Master volume",
  "settings.music": "Music",
  "settings.effects": "Effects",
  "settings.analytics": "Anonymous analytics",
  "settings.crashes": "Crash reports",
  "install.title": "Take the chamber offline",
  "install.detail":
    "Install STORMGLASS for a full-screen portrait experience and reliable offline launches.",
  "install.ios": "In Safari, tap Share, then Add to Home Screen.",
  "install.android":
    "Use Install app in your browser menu if the install button is unavailable.",
  "install.ready": "STORMGLASS is already running as an installed app.",
  "recovery.title": "The chamber recovered safely",
  "recovery.detail":
    "The newest save could not be read, so the last known-good checkpoint was restored.",
  "recovery.export": "Export diagnostics",
  "quality.auto": "Auto",
  "quality.high": "High",
  "quality.balanced": "Balanced",
  "quality.low": "Low",
  "weather.lightning": "Lightning",
  "weather.rain": "Rain",
  "weather.wind": "Wind",
  "weather.hail": "Hail",
} as const;

export type MessageId = keyof typeof EN;
export type Locale = "en";

const interpolate = (template: string, params: MessageParams): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = params[key];
    return value === undefined ? match : String(value);
  });

export class Localizer {
  readonly locale: Locale;

  constructor(locale: Locale = "en") {
    this.locale = locale;
  }

  t(id: MessageId, params: MessageParams = {}): string {
    return interpolate(EN[id], params);
  }

  number(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      notation: value >= 10_000 ? "compact" : "standard",
      maximumFractionDigits: 1,
    }).format(value);
  }

  percent(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(value);
  }
}

export const localizer = new Localizer();
